import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, CheckCircle2, Layers, Play, Shirt, Sunrise, Sunset, Timer } from 'lucide-react'
import { api } from '@/lib/api'
import { daysAgoInMalaysia, formatDateTime } from '@/lib/date'
import { BAR_RADIUS, CHART, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import { useAuth } from '@/context/AuthContext'
import type { MyStats } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import DateRangeBar from '@/components/report/DateRangeBar'
import LogoLoader from '@/components/ui/LogoLoader'
import { ChartBox } from '@/components/ui/Chart'
import { cn } from '@/lib/cn'
import { formatMinutes } from '@/lib/duration'

/**
 * The operator's own output over a date range - the same numbers the admin
 * operator report shows, scoped to the signed-in account. Stages worked as a
 * teammate on somebody else's completion count in full.
 */
export default function StaffStats() {
  const { user } = useAuth()
  const [from, setFrom] = useState(daysAgoInMalaysia(29))
  const [to, setTo] = useState(daysAgoInMalaysia(0))

  const { data, isLoading } = useQuery({
    queryKey: ['staff-stats', { from, to }],
    queryFn: async () =>
      (await api.get<{ data: MyStats }>('/staff/stats', { params: { from, to } })).data.data,
    // Keep the last range on screen while a new one loads, so tapping through
    // the presets does not flash the whole page to a skeleton.
    placeholderData: keepPreviousData,
  })

  const assisted = data?.stages_assisted ?? 0

  const tiles = [
    { label: 'Stages started', value: data?.stages_started, icon: Play },
    {
      label: 'Stages completed',
      value: data?.stages_completed,
      icon: CheckCircle2,
      hint: assisted > 0 ? `incl. ${assisted} assisted` : undefined,
    },
    { label: 'Issues flagged', value: data?.issues_flagged, icon: AlertTriangle, danger: true },
    { label: 'Jobs touched', value: data?.jobs_touched, icon: Layers },
    { label: 'Pieces completed', value: data?.pieces_completed, icon: Shirt },
    { label: 'Avg time per stage', value: formatMinutes(data?.avg_stage_minutes), icon: Timer },
    { label: 'Typical start', value: data?.avg_start_clock ?? '-', icon: Sunrise },
    { label: 'Typical finish', value: data?.avg_end_clock ?? '-', icon: Sunset },
  ]

  return (
    <div>
      <PageHeader
        title="My stats"
        subtitle={`Your output at ${user?.department?.name ?? 'your section'}, from the QR scan logs`}
      />

      <Card className="mb-5">
        <DateRangeBar
          from={from}
          to={to}
          onFrom={setFrom}
          onTo={setTo}
          onPreset={(range) => {
            setFrom(range.from)
            setTo(range.to)
          }}
        />
      </Card>

      {isLoading ? (
        <LogoLoader />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {tiles.map((tile) => (
              <Card key={tile.label}>
                <tile.icon
                  className={cn(
                    'size-5',
                    tile.danger && (tile.value ?? 0) > 0 ? 'text-status-danger' : 'text-primary',
                  )}
                />
                <p className="mt-3 text-2xl font-bold">{tile.value ?? 0}</p>
                <p className="mt-0.5 text-xs text-muted">{tile.label}</p>
                {tile.hint && <p className="mt-0.5 text-2xs text-muted">{tile.hint}</p>}
              </Card>
            ))}
          </div>

          <Card className="mt-4">
            <SectionLabel>Pieces completed per day</SectionLabel>
            <ChartBox height={240}>
              <BarChart data={data?.daily ?? []}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="label" {...xAxisProps} />
                <YAxis {...yAxisProps} />
                <Tooltip {...tooltipProps} />
                <Bar dataKey="pieces" fill={CHART.primary} radius={BAR_RADIUS} />
              </BarChart>
            </ChartBox>
          </Card>

          <Card className="mt-4">
            <SectionLabel>Recent activity</SectionLabel>
            <ol className="space-y-2 text-xs">
              {(data?.recent?.length ?? 0) === 0 && (
                <p className="py-4 text-center text-sm text-muted">No activity in this range.</p>
              )}
              {data?.recent?.map((row, index) => (
                <li key={index}>
                  <Link
                    to={`/staff/jobs/${row.qr_token}`}
                    className="flex items-center justify-between gap-3 rounded-lg bg-app px-3 py-2 hover:bg-foreground/5"
                  >
                    <span className="min-w-0 font-semibold">
                      <span className="font-mono">{row.job_code}</span>
                      <span className="ml-2 font-normal capitalize text-muted">
                        {row.action}
                        {row.department ? ' at ' + row.department : ''}
                      </span>
                      {(row.with?.length ?? 0) > 0 && (
                        <span className="block font-normal text-muted">
                          {row.assisted ? 'assisted ' : 'with '}
                          {row.with!.join(', ')}
                        </span>
                      )}
                      {row.reason && <span className="block font-normal text-status-danger">{row.reason}</span>}
                    </span>
                    <span className="shrink-0 text-muted">{formatDateTime(row.at)}</span>
                  </Link>
                </li>
              ))}
            </ol>
          </Card>
        </>
      )}
    </div>
  )
}
